import { Alert, Button, Card, CardContent, Spinner } from '@blink/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Banknote, CheckCircle2, QrCode, ReceiptText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ErrorAlert, Screen } from '@/components/common';
import { aed } from '@/components/parcel-bits';
import { useI18n } from '@/i18n';
import { api, type CashQr, type MyCash as MyCashView } from '@/lib/api';
import { useLiveUpdates } from '@/lib/live';
import { HelpTip } from '@/components/help-tip';

/**
 * My cash (plan 4.5, Phase 3 step 11): cash in hand, what came in today, the handover QR for the
 * cashier and the receipts. The screen updates by itself when the cashier confirms.
 */
export function MyCash() {
  const { t, f, formatTime } = useI18n();
  const navigate = useNavigate();
  useLiveUpdates();
  const cash = useQuery({ queryKey: ['courier', 'cash'], queryFn: () => api<MyCashView>('/courier/cash'), refetchOnWindowFocus: true });
  const [qr, setQr] = useState<(CashQr & { shownAt: number }) | null>(null);
  const [, tick] = useState(0);
  const [copied, setCopied] = useState(false);
  const makeQr = useMutation({
    mutationFn: () => api<CashQr>('/courier/cash/qr', { method: 'POST' }),
    onSuccess: (answer) => {
      setQr({ ...answer, shownAt: Date.now() });
      setCopied(false);
    },
  });

  // the cashier confirmed: a receipt newer than the code on screen
  const v = cash.data;
  const done = qr && v?.receipts[0] && new Date(v.receipts[0].at).getTime() >= qr.shownAt - 5_000 ? v.receipts[0] : null;
  const expired = qr ? new Date(qr.expiresAt).getTime() < Date.now() : false;
  useEffect(() => {
    if (!qr || done) return;
    const timer = setInterval(() => tick((n) => n + 1), 10_000);
    return () => clearInterval(timer);
  }, [qr, done]);

  return (
    <Screen
      title={<h1 className="flex items-center gap-1 text-lg font-semibold">{t.cash.title} <HelpTip id="courier.cash" /></h1>}
      actions={
        <Button variant="ghost" size="icon" className="order-first text-white hover:bg-white/10 hover:text-white" aria-label={t.parcel.back} onClick={() => navigate('/')} data-testid="back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
      }
    >
      <ErrorAlert error={cash.error ?? makeQr.error} />
      {cash.isLoading ? <Spinner /> : null}
      {v ? (
        <>
          <Card>
            <CardContent className="space-y-1 pt-5">
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Banknote className="h-4 w-4" />
                {t.cash.inHand}
              </p>
              <p className="text-3xl font-extrabold" data-testid="cash-in-hand">
                {aed(v.inHandFils)}
              </p>
              <p className="text-sm text-muted-foreground">{f(t.cash.today, { amount: aed(v.todayFils) })}</p>
            </CardContent>
          </Card>

          {v.earlierFils > 0 ? (
            v.routeStart.exception ? (
              <Alert variant="warning" data-testid="cash-earlier">
                {f(t.cash.earlierAllowed, { amount: aed(v.earlierFils), reason: v.routeStart.exception.reason })}
              </Alert>
            ) : (
              <Alert variant="destructive" data-testid="cash-earlier">
                {f(t.cash.earlier, { amount: aed(v.earlierFils) })}
              </Alert>
            )
          ) : null}

          {done ? (
            <Alert variant="success" data-testid="cash-handed-over">
              <p className="flex items-center gap-2 font-semibold">
                <CheckCircle2 className="h-5 w-5" />
                {f(t.cash.handedOver, { amount: aed(done.receivedFils), number: done.number })}
              </p>
              {done.expectedFils > done.receivedFils ? <p className="text-sm">{f(t.cash.leftWithYou, { amount: aed(done.expectedFils - done.receivedFils) })}</p> : null}
            </Alert>
          ) : qr && !expired ? (
            <Card data-testid="cash-qr">
              <CardContent className="space-y-3 pt-5 text-center">
                <p className="text-sm">{t.cash.showQr}</p>
                {/* the server draws the QR (one-use code); shown as a picture */}
                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(qr.svg)}`} alt={qr.code} data-code={qr.code} className="mx-auto w-64 max-w-full rounded-lg border bg-white p-2" />
                <p className="text-2xl font-extrabold">{aed(v.inHandFils)}</p>
                <p className="text-xs text-muted-foreground">{f(t.cash.validUntil, { time: formatTime(qr.expiresAt) })}</p>
                {/* no scanner at the desk: the cashier can type or paste the code */}
                <button type="button" className="mx-auto block break-all rounded border px-2 py-1 font-mono text-xs text-muted-foreground" onClick={() => void navigator.clipboard?.writeText(qr.code).then(() => setCopied(true), () => undefined)} data-testid="cash-code-text">
                  {qr.code} · {copied ? t.cash.copied : t.cash.copy}
                </button>
              </CardContent>
            </Card>
          ) : null}

          {!done && (!qr || expired) ? (
            v.inHandFils > 0 ? (
              <Button className="h-14 w-full text-base" onClick={() => makeQr.mutate()} disabled={makeQr.isPending} data-testid="cash-hand-over">
                <QrCode className="h-5 w-5" />
                {qr ? t.cash.newQr : t.cash.handOver}
              </Button>
            ) : (
              <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">{t.cash.nothing}</p>
            )
          ) : null}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-muted-foreground">{t.cash.collectedTitle}</h2>
            {v.collectedToday.length ? (
              <ul className="divide-y rounded-lg border bg-card text-sm">
                {v.collectedToday.map((c, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2">
                    <span className="font-mono">{c.awb ?? '—'}</span>
                    <span className="text-muted-foreground">{formatTime(c.at)}</span>
                    <span className="font-semibold">{aed(c.amountFils)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t.cash.noneCollected}</p>
            )}
          </section>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-muted-foreground">{t.cash.receiptsTitle}</h2>
            {v.receipts.length ? (
              <ul className="divide-y rounded-lg border bg-card text-sm" data-testid="cash-receipts">
                {v.receipts.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 px-3 py-2">
                    <ReceiptText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">{f(t.cash.receipt, { number: r.number, time: formatTime(r.at), by: r.receivedBy ?? '—' })}</span>
                    <span className="font-semibold">{aed(r.receivedFils)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t.cash.noReceipts}</p>
            )}
          </section>
        </>
      ) : null}
    </Screen>
  );
}
